import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { auth } from "./config/firebase";
import { ALLOWED_DOMAINS } from "./config/constants";
import Dashboard from "./components/Dashboard";
import Editor from "./components/Editor";
import {
  Server,
  ShieldCheck,
  Zap,
  Layout,
  ArrowRight,
  Lock,
} from "lucide-react";

const LoginScreen = ({ onLogin }: any) => (
  <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6 relative overflow-hidden transition-colors duration-200">
    {/* Decorative Background Elements */}
    <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-40 dark:opacity-20 pointer-events-none">
      <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-blue-200 dark:bg-blue-900 blur-[100px]"></div>
      <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] rounded-full bg-indigo-200 dark:bg-indigo-900 blur-[100px]"></div>
    </div>

    <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-gray-100 dark:border-slate-800 z-10 overflow-hidden backdrop-blur-sm">
      {/* Header Section */}
      <div className="bg-slate-50 dark:bg-slate-950/50 p-8 text-center border-b border-gray-100 dark:border-slate-800">
        <div className="w-16 h-16 bg-blue-600 rounded-2xl mx-auto flex items-center justify-center shadow-lg shadow-blue-500/30 mb-6 transform rotate-0">
          <Server className="text-white w-8 h-8" strokeWidth={2.5} />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight">
          Checklist App
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
          Case Management & Onboarding
        </p>
      </div>

      {/* Content Section */}
      <div className="p-8 space-y-8">
        {/* Feature Highlights */}
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
            <div className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg">
              <ShieldCheck size={16} />
            </div>
            <span className="text-sm font-medium">
              Secure Google Workspace Login
            </span>
          </div>
          <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
              <Zap size={16} />
            </div>
            <span className="text-sm font-medium">
              Automated Notion & HubSpot Sync
            </span>
          </div>
          <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
              <Layout size={16} />
            </div>
            <span className="text-sm font-medium">
              Dynamic Implementation Checklists
            </span>
          </div>
        </div>

        {/* Login Action */}
        <div className="space-y-4 pt-2">
          <button
            onClick={onLogin}
            className="group w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 p-4 rounded-xl font-bold text-sm shadow-xl shadow-slate-200 dark:shadow-none hover:shadow-2xl hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-3"
          >
            <img
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              alt="Google"
              className="w-5 h-5"
            />
            <span>Sign in with Google</span>
            <ArrowRight
              size={16}
              className="opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all"
            />
          </button>

          <div className="text-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              <Lock size={10} />
              Authorized Personnel Only
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="absolute bottom-6 text-center">
      <p className="text-[10px] text-slate-400 dark:text-slate-600 font-medium">
        © {new Date().getFullYear()} Taggrs.io • v2.1.0
      </p>
    </div>
  </div>
);

export default function App() {
  const [user, setUser] = useState<any>(null);
  const [view, setView] = useState("dashboard");
  const [currentCaseId, setCurrentCaseId] = useState<string | null>(null);

  // Handle initial URL load and browser navigation
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const caseId = params.get("caseId");

      if (caseId) {
        setCurrentCaseId(caseId);
        setView("editor");
      } else {
        setCurrentCaseId(null);
        setView("dashboard");
      }
    };

    // Run once on mount
    handleUrlChange();

    // Listen for back/forward button clicks
    window.addEventListener("popstate", handleUrlChange);
    return () => window.removeEventListener("popstate", handleUrlChange);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      // Allow specific domain or specific users if needed
      if (u && u.email) {
        const email = u.email.toLowerCase();
        const isAllowedDomain = ALLOWED_DOMAINS.some((d) =>
          email.endsWith(`@${d}`),
        );

        if (isAllowedDomain) {
          setUser(u);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Login failed", error);
      toast.error(
        "Login failed. Please ensure you are using a @taggrs.io account.",
      );
    }
  };

  if (!user)
    return (
      <>
        <Toaster position="top-right" />
        <LoginScreen onLogin={handleLogin} />
      </>
    );

  return (
    <>
      <Toaster position="top-right" />
      {view === "dashboard" ? (
        <Dashboard
          user={user}
          onNavigate={(caseId: string) => {
            if (caseId !== "new") {
              const newUrl = new URL(window.location.href);
              newUrl.searchParams.set("caseId", caseId);
              window.history.pushState({}, "", newUrl);
            }
            setCurrentCaseId(caseId);
            setView("editor");
          }}
        />
      ) : (
        <Editor
          user={user}
          caseId={currentCaseId}
          onBack={() => {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete("caseId");
            window.history.pushState({}, "", newUrl);
            setCurrentCaseId(null);
            setView("dashboard");
          }}
        />
      )}
    </>
  );
}
